import React, {
  createContext,
  FC,
  useContext,
} from "react";
import { ApolloError, useQuery } from "@apollo/client";
import {
  EmployeeProfilesQuery,
  EmployeeProfilesQueryVariables,
} from "generated/graphql";
import {
  initialEmployeeProfilesResponse,
  DPEmployeeProfiles,
  GetEmployeeProfilesQueryProps,
  initialQueryProps,
} from "Shared/types/EmployeeProfiles";
import queries from "Shared/queries";
import { useLocation, useHistory } from "./useRouter";
import { queryToProps } from "Api";
import { goToPageCreator } from "./useGoTo";
import InternalRoutes from "Shared/Constants/InternalRoutes";
import { useUserFilters } from "./useUserFilters";

type EmployeeProfilesQueryData = {
  employeeProfilesResponse: DPEmployeeProfiles;
  loading: boolean;
  error: ApolloError | undefined;
  queryProps: GetEmployeeProfilesQueryProps;
  goToPage: (queryProps: GetEmployeeProfilesQueryProps) => void;
};

const initialUsersQueryState: EmployeeProfilesQueryData = {
  employeeProfilesResponse: initialEmployeeProfilesResponse,
  loading: true,
  error: undefined,
  queryProps: { ...initialQueryProps },
  goToPage: () => {},
};

const EmployeeProfilesQueryContext = createContext(initialUsersQueryState);

export const EmployeeProfilesQueryProvider: FC = ({ children }) => {
  const { search } = useLocation();
  const { userFilters } = useUserFilters();

  const queryProps = search === "" ? initialQueryProps : queryToProps(search);

  const { data, loading, error } = useQuery<
    EmployeeProfilesQuery,
    EmployeeProfilesQueryVariables
  >(queries.GET_EMPLOYEE_PROFILES, {
    variables: {
      page: queryProps.page,
      search: queryProps.search,
      locationIds: userFilters.locationIds,
      userStatus: userFilters.userStatuses.join(","),
      employmentStatus: userFilters.employmentStatuses.join(","),
      companyIds: userFilters.payGroupIds,
      benefitType: userFilters.benefitTypes.join(","),
      sort: queryProps.sort.join(","),
    },
  });

  const history = useHistory();
  const goToPage = goToPageCreator(history, InternalRoutes.users);

  return (
    <EmployeeProfilesQueryContext.Provider
      value={{
        employeeProfilesResponse: data?.employeeProfiles || initialEmployeeProfilesResponse,
        error,
        goToPage,
        loading,
        queryProps,
      }}
    >
      {children}
    </EmployeeProfilesQueryContext.Provider>
  );
};

export const useEmployeeProfilesQuery = () =>
  useContext(EmployeeProfilesQueryContext);
